'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Search, X } from 'lucide-react';

import { PublicLink } from '@/components/PublicLink';
import { AnimatedContainer } from '@/components/ui/animated-container';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  buildCatalogQuery,
  filterTemplates,
  TEMPLATE_TAG_LABELS,
  TEMPLATE_TAG_ORDER,
} from '@/lib/templateCatalogFilters';
import { cn } from '@/lib/utils';
import { useSession } from 'next-auth/react';
import type { TemplateSourceTag, TemplateSummary } from '@/types/templates';
import {
  EmptyState,
  MarketingPageShell,
  PageHero,
  PageIntro,
  PageSection,
  SectionPill,
  SurfacePanel,
} from '@/components/site/page-shell';

type TemplatesClientProps = {
  initialTemplates: TemplateSummary[];
  initialError?: string | null;
  initialSearch?: string;
  initialSelectedTags?: TemplateSourceTag[];
};

function toOrderedTags(tags: TemplateSourceTag[]): TemplateSourceTag[] {
  const selected = new Set(tags);
  return TEMPLATE_TAG_ORDER.filter((tag) => selected.has(tag));
}

export function TemplatesClient({
  initialTemplates,
  initialError,
  initialSearch = '',
  initialSelectedTags = [],
}: TemplatesClientProps) {
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const templates = initialTemplates;
  const error = initialError ?? null;
  const [search, setSearch] = useState(initialSearch);
  const [selectedTags, setSelectedTags] = useState<TemplateSourceTag[]>(() =>
    toOrderedTags(initialSelectedTags),
  );

  useEffect(() => {
    setSearch(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    setSelectedTags(toOrderedTags(initialSelectedTags));
  }, [initialSelectedTags]);

  const filteredTemplates = useMemo(
    () => filterTemplates(templates, { search, tags: selectedTags }),
    [search, selectedTags, templates],
  );

  const query = useMemo(
    () => buildCatalogQuery({ search, tags: selectedTags }),
    [search, selectedTags],
  );

  const hasActiveFilters = search.trim().length > 0 || selectedTags.length > 0;

  useEffect(() => {
    const currentQuery = searchParams.toString();
    const nextQuery = query.toString();
    if (currentQuery === nextQuery) {
      return;
    }
    const href = nextQuery ? `${pathname}?${nextQuery}` : pathname;
    router.replace(href, { scroll: false });
  }, [pathname, query, router, searchParams]);

  const toggleTag = useCallback((tag: TemplateSourceTag) => {
    setSelectedTags((previous) => {
      const nextTags = previous.includes(tag)
        ? previous.filter((value) => value !== tag)
        : [...previous, tag];
      return toOrderedTags(nextTags);
    });
  }, []);

  const clearFilters = useCallback(() => {
    setSearch('');
    setSelectedTags([]);
  }, []);

  const clearSearch = useCallback(() => {
    setSearch('');
  }, []);

  return (
    <MarketingPageShell
      hero={
        <PageHero>
          <PageIntro
            eyebrow="Каталог шаблонов для парсинга"
            title="Шаблоны для парсинга и сбора данных с сайтов."
            description="Выбирайте готовые сценарии для парсинга маркетплейсов, объявлений и недвижимости. Просматривайте примеры результатов и запускайте извлечение в несколько кликов."
            actions={
              <div className="flex flex-wrap gap-2">
                <SectionPill>{templates.length} шаблонов</SectionPill>
                <SectionPill>Примеры результатов</SectionPill>
                <SectionPill>Парсинг маркетплейсов, Авито, Циан</SectionPill>
              </div>
            }
          />
        </PageHero>
      }
    >
      <PageSection className="gap-6">
        <AnimatedContainer animation="fadeIn" className="space-y-6">
          <SurfacePanel className="gap-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">Поиск шаблонов по категориям</h2>
                <p className="mt-2 text-sm leading-6 text-[var(--site-ink-muted)]">
                  Фильтруйте шаблоны по типу источника — маркетплейсы, объявления, недвижимость — или ищите по названию и полям.
                </p>
              </div>
              <div className="w-full md:w-80">
                <div className="relative">
                  <Search className="absolute left-4 top-3.5 h-4 w-4 text-[var(--site-ink-subtle)]" />
                  <label htmlFor="template-search" className="sr-only">
                    Поиск шаблонов
                  </label>
                  <Input
                    id="template-search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Поиск шаблонов"
                    className="pl-10 pr-16"
                  />
                  {search ? (
                    <button
                      type="button"
                      className="absolute right-4 top-3 inline-flex items-center gap-1 text-xs text-[var(--site-ink-subtle)] hover:text-[var(--site-ink-strong)]"
                      onClick={clearSearch}
                    >
                      <X className="h-3 w-3" />
                      Очистить
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                {TEMPLATE_TAG_ORDER.map((tag) => {
                  const active = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleTag(tag)}
                      className={cn(
                        'rounded-full border px-4 py-2 text-xs font-medium transition-colors',
                        active
                          ? 'border-[var(--site-primary-fill)] bg-[var(--site-primary-fill)] text-[var(--site-primary-text)]'
                          : 'border-[color:var(--site-border-soft)] bg-[var(--site-surface-strong)] text-[var(--site-ink)] hover:border-[color:var(--site-border-mid)] hover:bg-[var(--site-surface-hover)]',
                      )}
                    >
                      {TEMPLATE_TAG_LABELS[tag]}
                    </button>
                  );
                })}
                {hasActiveFilters ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 px-2 text-xs"
                    onClick={clearFilters}
                  >
                    Сбросить фильтры
                  </Button>
                ) : null}
              </div>
              {!error ? (
                <p className="text-sm text-[var(--site-ink-muted)]">
                  Показано {filteredTemplates.length} из {templates.length} шаблонов
                </p>
              ) : null}
            </div>
          </SurfacePanel>

          {error ? (
            <EmptyState title="Каталог шаблонов недоступен" description={error} />
          ) : filteredTemplates.length === 0 ? (
            <EmptyState
              title="По текущим фильтрам ничего не найдено."
              description="Попробуйте расширить поиск или убрать часть тегов."
              action={
                hasActiveFilters ? (
                  <Button type="button" variant="outline" className="w-fit" onClick={clearFilters}>
                    Сбросить фильтры
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {filteredTemplates.map((template) => (
                <Card key={template.id} className="flex h-full flex-col">
                  <CardHeader className="space-y-3">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <CardDescription className="text-sm">{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col gap-4">
                    <div className="flex flex-wrap gap-2">
                      {template.tags.map((tag) => (
                        <span
                          key={`${template.id}-${tag}`}
                          className="rounded-full border border-[color:var(--site-border-soft)] bg-[var(--site-surface-deep)] px-3 py-1 text-xs font-medium text-[var(--site-ink)]"
                        >
                          {TEMPLATE_TAG_LABELS[tag] ?? tag}
                        </span>
                      ))}
                      {template.requiresAuth ? (
                        <span className="rounded-full border border-[var(--site-accent-border)] bg-[var(--site-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--site-accent-text)]">
                          Требуется авторизация
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-auto flex flex-col gap-3">
                      <PublicLink href={`/templates/${template.id}`} className="block">
                        <Button className="w-full">Открыть шаблон</Button>
                      </PublicLink>
                      {!isAuthenticated ? (
                        <p className="text-xs text-[var(--site-ink-muted)]">
                          Войдите, чтобы запускать шаблоны и создавать датасеты.
                        </p>
                      ) : template.requiresAuth ? (
                        <p className="text-xs text-[var(--site-accent-text)]">
                          После входа такие шаблоны будут запускаться от имени вашего аккаунта.
                        </p>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </AnimatedContainer>
      </PageSection>
    </MarketingPageShell>
  );
}
